/**
 * Section placement rules — the single authority on whether a section may sit
 * where it is being put. Every way of changing a layout (dragging a handle,
 * typing a dimension, adding a section, changing its product) goes through
 * `checkPlacement`, and stored layouts are checked with `validateRoom` when
 * they are loaded.
 *
 * The rules (see types/sections.ts for the fill / insert model):
 *   - a section stays inside its surface and is at least 6" in each direction;
 *   - sections of the same fill mode never overlap (W&CB ↔ W&CB, SlatWall ↔
 *     SlatWall). A SlatWall may sit over W&CB — the resolver carves the boards;
 *   - SlatWall is whole 6" rows tall, and never goes on a ceiling.
 *
 * On a wall with a shaped top (`Surface.outline` — under a sloped ceiling) a
 * section may overhang the rake, since its boards are cut to it, but there must
 * be wall under it somewhere; a window or door must sit wholly inside the wall,
 * and its safe margin is measured to the slope above it.
 */

import { Opening, OpeningFloor, OpeningKind, Section, Surface, RoomSurfaces, SectionProductType, sectionFillMode, openingOnFloor, openingKeepsOffFloor, roomSurfaceList, roomSurfaceById } from '../../types/sections';
import { SLATWALL_COVERAGE_HEIGHT } from '../constants';
import { Rect, rectOf, intersectRect, subtractRects } from './resolver';
import { surfaceOutline, outlineTopRange } from './outline';

/** 6" — one SlatWall row, and the narrowest strip worth laying a board on. */
export const MIN_SECTION_SIZE_FT = 0.5;

const EPS = 1e-6;

/** 12" — the smallest window or door worth cutting. */
export const MIN_OPENING_SIZE_FT = 1;

export type PlacementIssue =
  | 'too-small'
  | 'out-of-bounds'
  | 'overlap'
  | 'slatwall-rows'
  | 'slatwall-on-ceiling'
  // openings
  | 'too-close-to-edge'
  | 'not-on-floor'
  | 'opening-on-ceiling';

export interface PlacementOptions {
  /** Section to leave out of the overlap check — the one being moved or resized. */
  ignoreId?: string;
  /**
   * Minimum distance (feet) to keep from the surface's edges. Zero for product
   * sections, which may run to the perimeter; openings use it for their safe margin.
   */
  edgeMargin?: number;
}

/** Is `value` a whole number of SlatWall rows (6" each)? */
export function isWholeSlatWallRows(height: number): boolean {
  const rows = height / SLATWALL_COVERAGE_HEIGHT;
  return rows >= 1 - EPS && Math.abs(rows - Math.round(rows)) < 1e-4;
}

/**
 * Check a rectangle for a section of `productType` on `surface`.
 * Returns the first rule it breaks, or null when the placement is fine.
 */
export function checkPlacement(
  surface: Surface,
  rect: Rect,
  productType: SectionProductType,
  options: PlacementOptions = {},
): PlacementIssue | null {
  const margin = options.edgeMargin ?? 0;

  if (productType === 'SlatWall' && surface.kind === 'ceiling') return 'slatwall-on-ceiling';
  if (rect.width < MIN_SECTION_SIZE_FT - EPS || rect.height < MIN_SECTION_SIZE_FT - EPS) return 'too-small';
  if (
    rect.x < margin - EPS
    || rect.y < margin - EPS
    || rect.x + rect.width > surface.width - margin + EPS
    || rect.y + rect.height > surface.height - margin + EPS
  ) return 'out-of-bounds';
  if (productType === 'SlatWall' && !isWholeSlatWallRows(rect.height)) return 'slatwall-rows';
  // Under a slope: something of the wall must rise above the section's bottom.
  const outline = surfaceOutline(surface);
  if (outline && outlineTopRange(outline, rect.x, rect.x + rect.width).max < rect.y + MIN_SECTION_SIZE_FT - EPS) return 'out-of-bounds';

  const mode = sectionFillMode(productType);
  for (const other of surface.sections) {
    if (other.id === options.ignoreId) continue;
    if (sectionFillMode(other.productType) !== mode) continue;
    if (intersectRect(rect, rectOf(other))) return 'overlap';
  }
  return null;
}

export const openingRect = (o: Opening): Rect => ({ x: o.x, y: o.y, width: o.width, height: o.height });

export interface OpeningPlacementOptions {
  /** Opening to leave out of the overlap check — the one being moved or resized. */
  ignoreId?: string;
  /**
   * Safe margin (feet): the least distance to keep between the opening and the
   * wall's perimeter. It never applies to the bottom edge of a door or garage
   * door: pinned, it stands on the floor; unpinned, it may still come down to it.
   */
  margin?: number;
}

/**
 * Check a rectangle for a window or door on `surface`. Returns the first rule
 * it breaks, or null when the placement is fine.
 *
 * Openings may sit over any product — they cut through it — so the only things
 * an opening can collide with are the wall's edges and other openings.
 */
export function checkOpeningPlacement(
  surface: Surface,
  rect: Rect,
  opening: OpeningKind | OpeningFloor,
  options: OpeningPlacementOptions = {},
): PlacementIssue | null {
  const margin = options.margin ?? 0;
  const onFloor = openingOnFloor(opening);

  if (surface.kind === 'ceiling') return 'opening-on-ceiling';
  if (rect.width < MIN_OPENING_SIZE_FT - EPS || rect.height < MIN_OPENING_SIZE_FT - EPS) return 'too-small';
  if (onFloor && Math.abs(rect.y) > EPS) return 'not-on-floor';
  if (
    rect.x < -EPS
    || rect.y < -EPS
    || rect.x + rect.width > surface.width + EPS
    || rect.y + rect.height > surface.height + EPS
  ) return 'out-of-bounds';
  if (
    rect.x < margin - EPS
    || rect.x + rect.width > surface.width - margin + EPS
    || rect.y + rect.height > surface.height - margin + EPS
    || (openingKeepsOffFloor(opening) && rect.y < margin - EPS)
  ) return 'too-close-to-edge';
  // Under a slope the wall's top is lower than `surface.height` away from the
  // peak: the opening stays under it, with its margin, over its whole width.
  const outline = surfaceOutline(surface);
  if (outline) {
    const lowest = outlineTopRange(outline, rect.x, rect.x + rect.width).min;
    if (rect.y + rect.height > lowest + EPS) return 'out-of-bounds';
    if (rect.y + rect.height > lowest - margin + EPS) return 'too-close-to-edge';
  }

  for (const other of surface.openings ?? []) {
    if (other.id === options.ignoreId) continue;
    if (intersectRect(rect, openingRect(other))) return 'overlap';
  }
  return null;
}

export interface SectionIssue {
  surfaceId: string;
  sectionId: string;
  issue: PlacementIssue;
}

/**
 * Every section or opening on the surface that breaks a placement rule (empty
 * when the surface is sound). Openings are checked for being a valid opening at
 * all — not against the safe margin, which is a rule about where one may be
 * PLACED and can change after a layout was saved.
 */
export function validateSurface(surface: Surface): SectionIssue[] {
  const issues: SectionIssue[] = [];
  for (const section of surface.sections) {
    const issue = checkPlacement(surface, rectOf(section), section.productType, { ignoreId: section.id });
    if (issue) issues.push({ surfaceId: surface.id, sectionId: section.id, issue });
  }
  for (const opening of surface.openings ?? []) {
    const issue = checkOpeningPlacement(surface, openingRect(opening), opening, { ignoreId: opening.id });
    if (issue) issues.push({ surfaceId: surface.id, sectionId: opening.id, issue });
  }
  return issues;
}

export function validateRoom(room: RoomSurfaces): SectionIssue[] {
  return roomSurfaceList(room).flatMap(validateSurface);
}

/**
 * The bare areas of a surface: everything no section covers, as non-overlapping
 * rectangles. Slivers too small to hold a section (under `minSize` either way —
 * e.g. the 1" expansion gap above a SlatWall band) are left out, so every
 * rectangle returned is somewhere a new section could go.
 */
export function computeGaps(surface: Surface, minSize: number = MIN_SECTION_SIZE_FT): Rect[] {
  const whole: Rect = { x: 0, y: 0, width: surface.width, height: surface.height };
  const outline = surfaceOutline(surface);
  // An opening is not bare wall either — nothing can be added inside a window or a door.
  return subtractRects(whole, [...surface.sections.map(rectOf), ...(surface.openings ?? []).map(openingRect)])
    // Under a slope, a gap reaches only as high as the wall does over its width.
    .map((r) => (outline ? { ...r, height: Math.min(r.y + r.height, outlineTopRange(outline, r.x, r.x + r.width).max) - r.y } : r))
    .filter((r) => r.width >= minSize - EPS && r.height >= minSize - EPS);
}

/** The surface an opening id lives on, with the opening — or null. */
export function findOpening(room: RoomSurfaces, surfaceId: string, openingId: string): { surface: Surface; opening: Opening } | null {
  const surface = roomSurfaceById(room, surfaceId);
  const opening = surface?.openings?.find((o) => o.id === openingId);
  return surface && opening ? { surface, opening } : null;
}

/** The surface a section id lives on, with the section — or null. */
export function findSection(room: RoomSurfaces, surfaceId: string, sectionId: string): { surface: Surface; section: Section } | null {
  const surface = roomSurfaceById(room, surfaceId);
  const section = surface?.sections.find((s) => s.id === sectionId);
  return surface && section ? { surface, section } : null;
}
